import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  GetPaymentListParams,
  getPaymentList,
  Payment,
  deleteFinancialRecord,
} from '$api/finance/paymentList';
import toast from '~/utils/toast';
import {ToastType} from '~/components/Toast/constant';
import {handleErrorResponse} from '~/utils/axios';

const DEFAULT_ERROR_MESSAGE = 'Could not get payments list!';
const DEFAULT_PAGE_SIZE = 10;

export interface Patient {
  id: number;
  full_name: string;
  uniq_code: string;
  mobile_number: string;
  dob: string;
  email: string;
  profile_url: string;
}

interface PaymentListResponse {
  isLoading: boolean;
  isError: boolean;
  message: string;
  payments: Payment[];
  patient?: Patient;
  totalPages: number;
  totalRecords: number;
  success: boolean;
  all_payment_count: number;
  paid_payment_count: number;
  unpaid_payment_count: number;
  min_amount_range: number;
  max_amount_range: number;
}

const getQueryKey = (params: GetPaymentListParams) => [
  'payment-list',
  params.search,
  params.status,
  params.orderBy,
  params.pageNumber,
  params.orderDirection,
  params.fromDate,
  params.toDate,
  params.patientId,
  params.perPage || DEFAULT_PAGE_SIZE,
  params.min_amount,
  params.max_amount,
  params.payment_method,
];

export function usePaymentList(
  params: GetPaymentListParams = {}
): PaymentListResponse {
  const {data, isError, isLoading, isRefetching} = useQuery({
    queryKey: getQueryKey(params),
    queryFn: () => getPaymentList(params),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: isLoading || isRefetching,
    isError,
    message: data?.message || DEFAULT_ERROR_MESSAGE,
    payments: data?.payments || [],
    totalPages: data?.totalPages || 0,
    totalRecords: data?.totalRecords || 0,
    success: data?.success || false,
    all_payment_count: data?.all_payment_count || 0,
    paid_payment_count: data?.paid_payment_count || 0,
    unpaid_payment_count: data?.unpaid_payment_count || 0,
    patient: params.patientId ? data?.patient : undefined,
    min_amount_range: data?.min_amount_range || 0,
    max_amount_range: data?.max_amount_range || 400000,
  };
}

export function useDeleteFinancialRecord() {
  const queryClient = useQueryClient();
  const {isPending, mutate} = useMutation({
    mutationKey: ['delete-financial-record'],
    mutationFn: deleteFinancialRecord,
    onSuccess: data => {
      toast(ToastType.Success, 'Financial record deleted', data.message);
      queryClient.invalidateQueries({queryKey: ['payment-list']});
    },
    onError: error => {
      handleErrorResponse(error, 'Delete financial record');
    },
  });

  const requestDeleteAppointment = (id: number) => {
    mutate(id);
  };

  return {
    isPending,
    requestDeleteAppointment,
  };
}
